"use client";

import { useState, useMemo, useCallback } from "react";
import { ArrowRightLeft, Building, Search, Check, ChevronRight, ChevronDown } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { SimplifiedMFAModal } from "@/app/_module/components/SimplifiedMFAModal";
import { verifyMFAForAction, resendSMSForAction } from "@/app/_module/services/auth";
import { useToast } from "@/app/_module/hooks/use-toast";

import { useGetSubAgencies, useMoveUser } from "../../hooks/use-hook";
import type { AgencyUserWithAgency, SubAgency, MoveUserResponse } from "../../types/types";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface MoveAgentModalProps {
  agencyUser: AgencyUserWithAgency;
  currentAgencyId: string;
  currentAgencyName: string;
  trigger: React.ReactNode;
}

// Agency with depth info for hierarchical display
interface AgencyWithDepth {
  agency: SubAgency;
  depth: number;
}

export const MoveAgentModal = function({
  agencyUser,
  currentAgencyId,
  currentAgencyName,
  trigger,
}: MoveAgentModalProps) {
  const [open, setOpen] = useState(false);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [moveResult, setMoveResult] = useState<MoveUserResponse['data'] | null>(null);
  const { toast } = useToast();

  const moveUserMutation = useMoveUser();

  // Fetch all descendant sub-agencies (API returns full hierarchy)
  const { data: allSubAgenciesData, isLoading: isLoadingChildren } = useGetSubAgencies({
    parentAgencyId: currentAgencyId,
    page: 1,
    limit: 100,
  });

  // Normalize the sub-agencies data
  const normalize = (input: unknown): SubAgency[] => {
    if (Array.isArray(input)) return input.filter(item => item != null) as SubAgency[];
    if (input && typeof input === "object") {
      const obj = input as Record<string, unknown>;
      if (Array.isArray(obj.items)) return obj.items.filter(item => item != null) as SubAgency[];
      if (obj.data && typeof obj.data === "object") {
        const d = obj.data as Record<string, unknown>;
        if (Array.isArray(d.items)) return d.items.filter(item => item != null) as SubAgency[];
      }
    }
    return [];
  };

  // Build a hierarchical tree structure from all descendants
  const { allAgenciesFlat, childrenMap } = useMemo(() => {
    const allAgencies = normalize(allSubAgenciesData).filter(a => a.isActive);

    // Build a map of parentId -> children
    const cMap = new Map<string, SubAgency[]>();
    for (const agency of allAgencies) {
      const parentId = agency.parentAgencyId;
      if (!cMap.has(parentId)) {
        cMap.set(parentId, []);
      }
      cMap.get(parentId)!.push(agency);
    }

    // DFS to flatten with depth (used for selection lookup and search)
    const result: AgencyWithDepth[] = [];
    const traverse = (parentId: string, depth: number) => {
      const children = cMap.get(parentId) || [];
      for (const child of children) {
        result.push({ agency: child, depth });
        traverse(child.id, depth + 1);
      }
    };
    traverse(currentAgencyId, 0);

    return { allAgenciesFlat: result, childrenMap: cMap };
  }, [allSubAgenciesData, currentAgencyId]);

  // Check if an agency has children
  const hasChildren = useCallback((agencyId: string) => {
    return (childrenMap.get(agencyId)?.length || 0) > 0;
  }, [childrenMap]);

  // Toggle expand/collapse for an agency
  const toggleExpand = useCallback((agencyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(agencyId)) {
        next.delete(agencyId);
      } else {
        next.add(agencyId);
      }
      return next;
    });
  }, []);

  // Build visible list: only show agencies whose entire ancestor chain is expanded
  // When searching, show all matching results flat (ignore expand state)
  const filteredAgencies = useMemo(() => {
    const isSearching = searchTerm.trim().length > 0;

    if (isSearching) {
      // When searching, show flat matching results
      return allAgenciesFlat.filter(({ agency }) =>
        agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Not searching: show tree with expand/collapse
    const visible: AgencyWithDepth[] = [];
    const traverse = (parentId: string, depth: number) => {
      const children = childrenMap.get(parentId) || [];
      for (const child of children) {
        visible.push({ agency: child, depth });
        // Only recurse into children if this node is expanded
        if (expandedIds.has(child.id)) {
          traverse(child.id, depth + 1);
        }
      }
    };
    traverse(currentAgencyId, 0);
    return visible;
  }, [allAgenciesFlat, childrenMap, currentAgencyId, expandedIds, searchTerm]);

  const selectedAgency = allAgenciesFlat.find(a => a.agency.id === selectedAgencyId)?.agency;

  const handleMoveClick = () => {
    if (!selectedAgencyId) return;
    setShowMFAModal(true);
  };

  const handleMFAVerify = async (code: string, method: 'totp' | 'sms'): Promise<boolean> => {
    try {
      return await verifyMFAForAction(code, method);
    } catch {
      return false;
    }
  };

  const handleMFAVerified = async () => {
    if (!selectedAgencyId) return;

    // DEMO SANDBOX: moving an agent rewrites agency membership, which the next visitor
    // to this seat would inherit. Gated here rather than at the button so the visitor
    // still sees the whole flow.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Moving agents between agencies is disabled in the demo sandbox. Start a free trial to manage your real team.",
        }),
      );
      return;
    }

    setIsMoving(true);
    try {
      const targetRole = agencyUser.role === 'owner' ? 'agent' : agencyUser.role;

      const result = await moveUserMutation.mutateAsync({
        sourceAgencyId: currentAgencyId,
        data: {
          userId: agencyUser.userId,
          targetAgencyId: selectedAgencyId,
          role: targetRole,
        },
      });

      // Extract transfer data from the response
      const transferData = result?.data || (result as any);
      setMoveResult(transferData);

      const parts: string[] = [];
      if (transferData?.leadsTransferred > 0) parts.push(`${transferData.leadsTransferred} leads`);
      if (transferData?.appointmentsTransferred > 0) parts.push(`${transferData.appointmentsTransferred} appointments`);
      if (transferData?.remindersTransferred > 0) parts.push(`${transferData.remindersTransferred} reminders`);

      toast({
        title: "Agent moved successfully",
        description: `${agencyUser.user.name} has been moved to ${selectedAgency?.name || 'the selected agency'}${parts.length > 0 ? `. Transferred: ${parts.join(', ')}.` : '.'}`,
      });

      setOpen(false);
      setSelectedAgencyId(null);
      setSearchTerm("");
      setExpandedIds(new Set());
      setMoveResult(null);
    } catch {
      toast({
        title: "Error",
        description: "Failed to move agent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedAgencyId(null);
    setSearchTerm("");
    setExpandedIds(new Set());
    setMoveResult(null);
  };

  return (
    <>
      <div onClick={() => setOpen(true)}>
        {trigger}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-primary" />
              Move Agent
            </DialogTitle>
            <DialogDescription>
              Move <span className="font-medium text-foreground">{agencyUser.user.name}</span> from{" "}
              <span className="font-medium text-foreground">{currentAgencyName}</span> to a sub-agency. Their leads, appointments, and reminders will be transferred.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search sub-agencies..."
                value={searchTerm}
              />
            </div>

            {/* Agency List */}
            <div className="max-h-60 overflow-y-auto border rounded-md">
              {isLoadingChildren ? (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  Loading sub-agencies...
                </div>
              ) : filteredAgencies.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                  {searchTerm ? "No sub-agencies match your search" : "No sub-agencies available to move to"}
                </div>
              ) : (
                filteredAgencies.map(({ agency, depth }) => {
                  const isSearching = searchTerm.trim().length > 0;
                  const agencyHasChildren = hasChildren(agency.id);
                  const isExpanded = expandedIds.has(agency.id);

                  return (
                    <button
                      key={agency.id}
                      type="button"
                      className={`w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b last:border-b-0 ${
                        selectedAgencyId === agency.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                      }`}
                      style={{ paddingLeft: `${16 + depth * 20}px` }}
                      onClick={() => setSelectedAgencyId(agency.id)}
                    >
                      {/* Expand/collapse toggle */}
                      {!isSearching && agencyHasChildren ? (
                        <span
                          role="button"
                          className="flex-shrink-0 p-0.5 rounded hover:bg-gray-200 transition-colors"
                          onClick={(e) => toggleExpand(agency.id, e)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </span>
                      ) : (
                        <span className="w-4.5 flex-shrink-0" />
                      )}
                      <Building className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{agency.name}</p>
                        {agency.description && (
                          <p className="text-xs text-muted-foreground truncate">{agency.description}</p>
                        )}
                      </div>
                      {selectedAgencyId === agency.id && (
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {selectedAgency && (
              <div className="bg-muted/50 rounded-md p-3 text-sm space-y-2">
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">{agencyUser.user.name}</span> will be moved from{" "}
                  <span className="font-medium text-foreground">{currentAgencyName}</span> to{" "}
                  <span className="font-medium text-foreground">{selectedAgency.name}</span> as an{" "}
                  <span className="font-medium text-foreground">{agencyUser.role === 'owner' ? 'Agent' : agencyUser.role === 'admin' ? 'Agency Admin' : 'Agent'}</span>.
                </p>
                <p className="text-xs text-muted-foreground">
                  All their leads, appointments, and reminders will be transferred to the new agency.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleClose} disabled={isMoving}>
              Cancel
            </Button>
            <Button
              onClick={handleMoveClick}
              disabled={!selectedAgencyId || isMoving}
            >
              {isMoving ? "Moving..." : "Move Agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MFA Verification */}
      <SimplifiedMFAModal
        isOpen={showMFAModal}
        onClose={() => setShowMFAModal(false)}
        onVerified={handleMFAVerified}
        onVerify={handleMFAVerify}
        onSendSMS={resendSMSForAction}
        title="Verify to Move Agent"
        description={`Please verify your identity with 2FA to move ${agencyUser.user.name} and all their data to ${selectedAgency?.name || 'the selected agency'}.`}
      />
    </>
  );
};

export default MoveAgentModal;
